import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createFeePreset,
  deleteFeePreset,
  listFeePresets,
  updateFeePreset,
} from '@/lib/api/fee-presets.js';
import { formatCurrency } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

export function FeePresetsSettings() {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [type, setType] = useState<'flat' | 'per_hour'>('flat');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');

  const presetsQuery = useQuery({
    queryKey: ['fee-presets'],
    queryFn: () => listFeePresets({ includeInactive: true }),
  });

  const createMut = useMutation({
    mutationFn: () =>
      createFeePreset({
        name: name.trim(),
        type,
        amount: Number(amount),
        description: description.trim() || undefined,
      }),
    onSuccess: () => {
      setName('');
      setAmount('');
      setDescription('');
      queryClient.invalidateQueries({ queryKey: ['fee-presets'] });
      toastSuccess('Fee preset added');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteFeePreset(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fee-presets'] });
      toastSuccess('Fee preset removed');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  return (
    <section className="mt-8 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="text-lg font-semibold text-slate-950">Fee Presets</h3>
      <p className="mt-1 text-sm text-slate-600">
        Quick-add fees on tickets (rush fee, diagnostic, labor, etc.) — not tied to a device or repair service.
      </p>

      <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(presetsQuery.data ?? []).filter((p) => p.active).map((preset) => (
              <tr key={preset.id}>
                <td className="px-4 py-3 font-semibold">{preset.name}</td>
                <td className="px-4 py-3">{formatCurrency(preset.amount)}</td>
                <td className="px-4 py-3 capitalize">{preset.type.replace('_', ' ')}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    className="text-sm font-semibold text-red-700 hover:underline"
                    onClick={() => deleteMut.mutate(preset.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 grid gap-3 rounded-xl bg-slate-50 p-4 md:grid-cols-2 lg:grid-cols-4">
        <input
          className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
          placeholder="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <select className="rounded-xl border border-slate-300 px-3 py-2 text-sm" value={type} onChange={(e) => setType(e.target.value as 'flat' | 'per_hour')}>
          <option value="flat">Flat</option>
          <option value="per_hour">Per Hour</option>
        </select>
        <input
          type="number"
          min="0"
          step="0.01"
          className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
          placeholder="Amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <input
          className="rounded-xl border border-slate-300 px-3 py-2 text-sm md:col-span-2 lg:col-span-1"
          placeholder="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <button
          type="button"
          className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 lg:col-span-3"
          disabled={!name.trim() || !amount || createMut.isPending}
          onClick={() => createMut.mutate()}
        >
          Add Fee Preset
        </button>
      </div>
    </section>
  );
}
